/**
 * Absolute depth magnitude (in %) a user can toggle in the depth view options,
 * as a numeric string (e.g. `'0.5'`, `'1'`, `'3'`, `'12'`). The set is dynamic —
 * driven by what the server sends, not a fixed union — so any depth flows through
 * without a type change. Each level maps to a symmetric ± pair of `ActualDepth`
 * values from `@skynet/data-liquidity-types` (e.g. `'2'` → `'2'` and `'-2'`).
 */
export type DepthLevel = string;

/**
 * Always-present base set: these levels are shown in the filter and enabled by
 * default for every domain, even when the server payload omits them. Anything a
 * domain's payload carries beyond this base is added as an extra (off-by-default)
 * toggle — see `getAvailableDepthLevels`.
 *
 * Because the base is unconditional, a domain may offer a base level its payload
 * never carries (e.g. retainers historically receive only ±2/±5/±10, so ±1 is
 * offered with no data). Toggling such a level is a deliberate no-op: it renders
 * no rows until the server starts sending that depth. This is by design — the
 * base is the standard, domain-agnostic option set — not a rendering bug.
 */
export const BASE_DEPTH_LEVELS: readonly DepthLevel[] = ['1', '2', '5'];

/**
 * Persisted depth view-options state for a single domain (loans, retainers, …).
 * Only the enabled levels are rendered in that domain's liquidity table and its
 * tasks.
 */
export interface DepthViewOptionsState {
  /** Enabled depth magnitudes, ascending. Empty means no depth rows are shown. */
  enabledDepths: DepthLevel[];
}
